// In-page preview of one course item (Course view): PDF, audio, video and image files play / show here
// through a signed link, games load in a frame, quizzes list their questions with the correct answers,
// and text / task items show their text.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ExternalLink, Pencil } from 'lucide-react';
import LoadingState from '../../components/common/LoadingState';
import { CvDialog } from './CourseStructureForms';
import { FILE_TYPES, ITEM_TYPES, byOrder, capitalise, itemDetails, linkUrlOf, plural, quizOf, viewableUrl } from './courseViewShared';

const QUESTION_TYPES = { mcq_single: 'Single choice', mcq_multiple: 'Multiple choice', true_false: 'True / False', fill_blank: 'Fill in the blank' };

// Quiz questions (Quiz model) and the older inline chapter questions ({ question, options: [String], correctAnswer }).
function questionOptions(q) {
  if (q.type === 'true_false') return [true, false].map((v) => ({ text: v ? 'True' : 'False', isCorrect: q.correctAnswer === v }));
  return (q.options || []).map((o) => (typeof o === 'string' ? { text: o, isCorrect: q.correctAnswer != null && String(q.correctAnswer) === o } : o));
}

function QuizQuestions({ questions }) {
  if (!questions.length) return <p className="cv-preview__note">This quiz has no questions yet.</p>;
  return (
    <ol className="cv-quiz">
      {questions.map((q, i) => {
        const options = questionOptions(q);
        return (
          <li key={q._id || i} className="cv-quiz__q">
            <div className="cv-quiz__head">
              <b>{i + 1}. {q.questionText || q.question}</b>
              {q.type && <span className="cm-pill cm-pill--gray">{QUESTION_TYPES[q.type] || q.type}</span>}
              {q.points != null && <span className="cv-quiz__pts">{plural(q.points, 'point')}</span>}
            </div>
            {options.length > 0 && (
              <ul className="cv-quiz__opts">
                {options.map((o, j) => (
                  <li key={j} className={o.isCorrect ? 'is-correct' : ''}>
                    <span>{o.text}</span>
                    {o.isCorrect && <small>✓ Correct</small>}
                  </li>
                ))}
              </ul>
            )}
            {q.type === 'fill_blank' && q.acceptedAnswers?.length > 0 && (
              <p className="cv-quiz__answers">Accepted answers: <b>{q.acceptedAnswers.join(', ')}</b></p>
            )}
            {q.explanation && <p className="cv-quiz__explain">{q.explanation}</p>}
          </li>
        );
      })}
    </ol>
  );
}

export default function CourseItemPreview({ item, onClose }) {
  const navigate = useNavigate();
  const type = ITEM_TYPES[item.type] || ITEM_TYPES.file;
  const isFile = FILE_TYPES.includes(item.type);
  const rawUrl = item.type === 'link' ? linkUrlOf(item) : item.fileUrl || '';
  const [url, setUrl] = useState(isFile ? '' : rawUrl);
  const [loading, setLoading] = useState(isFile && Boolean(rawUrl));
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!isFile || !rawUrl) return undefined;
    let alive = true;
    viewableUrl(rawUrl).then((signed) => {
      if (!alive) return;
      setUrl(signed);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [isFile, rawUrl]);

  const quiz = quizOf(item);
  const details = itemDetails(item);

  let body;
  if (loading) {
    body = <LoadingState message="Opening file…" />;
  } else if ((isFile || item.type === 'link') && !url) {
    body = <p className="cv-preview__note">No file or link has been added to this item yet.</p>;
  } else if (failed) {
    body = (
      <div className="cv-preview__error" role="alert">
        <AlertCircle size={30} />
        <p>This file could not be shown here.</p>
        <small>It may have been moved or deleted, or this browser cannot play its format. Try “Open in new tab”.</small>
      </div>
    );
  } else if (item.type === 'pdf') {
    body = <iframe className="cv-preview__frame" src={url} title={item.title} />;
  } else if (item.type === 'video') {
    body = <video className="cv-preview__media" src={url} controls preload="metadata" onError={() => setFailed(true)} aria-label={item.title} />;
  } else if (item.type === 'audio') {
    body = (
      <div className="cv-preview__audio">
        <span aria-hidden="true">🎧</span>
        <audio src={url} controls preload="metadata" onError={() => setFailed(true)} aria-label={item.title} />
      </div>
    );
  } else if (item.type === 'image') {
    body = <img className="cv-preview__img" src={url} alt={item.title} onError={() => setFailed(true)} />;
  } else if (item.type === 'link') {
    body = (
      <>
        <iframe className="cv-preview__frame" src={url} title={item.title} allow="autoplay; fullscreen" />
        <p className="cv-preview__hint">If the game stays blank, its site does not allow being shown inside another page. Use “Open in new tab”.</p>
      </>
    );
  } else if (item.type === 'quiz') {
    const questions = byOrder(quiz?.questions || item.quizData?.questions || []);
    body = quiz || item.quizData?.questions?.length ? (
      <div className="cv-preview__quiz">
        {quiz && (
          <div className="cv-preview__quizhead">
            <b>{quiz.title}</b>
            {quiz.status && <span className={`cm-pill cm-pill--${quiz.status === 'published' ? 'green' : 'amber'}`}>{capitalise(quiz.status)}</span>}
            <small>{plural(questions.length, 'question')}</small>
          </div>
        )}
        {quiz?.description && <p className="cv-preview__note">{quiz.description}</p>}
        <QuizQuestions questions={questions} />
      </div>
    ) : (
      <p className="cv-preview__note">This quiz is no longer available. It may have been deleted from Quiz Management.</p>
    );
  } else if (item.type === 'task') {
    body = (
      <div className="cv-preview__text">
        {item.taskData?.submissionType && <p><b>Submission:</b> {capitalise(item.taskData.submissionType)}</p>}
        <p>{item.taskData?.instructions || item.description || 'No instructions added.'}</p>
      </div>
    );
  } else {
    body = <div className="cv-preview__text"><p>{item.textContent || item.description || 'No text added.'}</p></div>;
  }

  return (
    <CvDialog
      title={item.title}
      subtitle={[`${type.icon} ${type.label}`, details].filter(Boolean).join(' · ')}
      label={`Preview ${item.title}`}
      wide
      dismissible
      onClose={onClose}
    >
      <div className="cv-dialog__body cv-preview">{body}</div>
      <footer className="cv-dialog__foot">
        {url && (isFile || item.type === 'link') && (
          <a className="cm-btn cm-btn--ghost cm-btn--sm" href={url} target="_blank" rel="noopener noreferrer"><ExternalLink size={15} />Open in new tab</a>
        )}
        {quiz?._id && (
          <button type="button" className="cm-btn cm-btn--soft cm-btn--sm" onClick={() => navigate(`/admin/quizzes/${quiz._id}/edit`)}><Pencil size={15} />Edit quiz</button>
        )}
        <button type="button" className="cm-btn cm-btn--purple cm-btn--sm" onClick={onClose}>Close</button>
      </footer>
    </CvDialog>
  );
}
